import { NgIf, AsyncPipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { PageDetailComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { AgrUser, SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import {
  getCustomerProducts,
  getProducts,
  getSelectedProductCountry,
} from '../../../product/store/selectors/product.selectors';
import { EnergySystem, LysineSystem } from '../../../settings/models/settings.model';
import {
  getEnergySetting,
  getLysineSetting,
} from '../../../settings/store/selectors/settings.selectors';
import { Product } from '../../../shared/models/shared.model';
import {
  FeedAdvice,
  FeedAdviceLine,
  FeedAdviceProperties,
  FeedAdviceType,
  FeedIntakeTypes,
  FeedingType,
  GenderTypes,
  GeneticLineType,
  InputSystemType,
  PhaseTransitionTypes,
  TableScheme,
  TerminalBoar,
} from '../../models/feed-advice.model';
import { SchemeSupportService } from '../../services/scheme-support.service';
import { upsertFeedAdviceRequest } from '../../store/feed-advice.actions';
import {
  getSelectedFeedAdvice,
  getSelectedFeedAdviceType,
  getSelectedLocations,
} from '../../store/feed-advice.selectors';
import { FeedAdviceSettingsComponent } from '../feed-advice-settings/feed-advice-settings.component';
import { FeedAdviceTableComponent } from '../feed-advice-table/feed-advice-table.component';
import { PriceSettingsComponent } from '../price-settings/price-settings.component';

@Component({
  selector: 'app-create-feed-advice',
  templateUrl: './create-feed-advice.component.html',
  styleUrls: ['./create-feed-advice.component.scss'],
  imports: [
    PageDetailComponent,
    MatButton,
    MatIcon,
    NgIf,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    FeedAdviceSettingsComponent,
    FeedAdviceTableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class CreateFeedAdviceComponent {
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  energySetting$: Observable<EnergySystem> = this.store.select(getEnergySetting);
  lysineSetting$: Observable<LysineSystem> = this.store.select(getLysineSetting);
  selectedFeedAdviceType$: Observable<FeedAdviceType> =
    this.store.select(getSelectedFeedAdviceType);
  products$: Observable<Product[]> = this.store.select(getProducts).pipe(
    withLatestFrom(
      this.store.select(getCustomerProducts),
      this.store.select(getSelectedProductCountry),
      this.store.select(UserSelectors.selectLocation),
    ),
    skipWhile(
      ([existingProducts, customerProducts]) =>
        existingProducts === undefined || customerProducts === undefined,
    ),
    map(([products, customerProducts, selectedCountry, location]) => {
      const effectiveCountry = selectedCountry ?? location?.country;
      const mergedProducts = [...(products ?? []), ...(customerProducts ?? [])].map(
        (product) =>
          ({
            id: product.id,
            code: product.code,
            name: product.name,
            suitablePhases: product.suitablePhases,
            ewKg: product.ewKg,
            lysKg: product.lysKg,
            customerId: product.customerId,
            country: product.country,
            energySystem: product.energySystem,
            lysineSystem: product.lysineSystem,
            unitNettoPrice: product.unitNettoPrice,
            currency: product.currency,
            schemeTypes: product.schemeTypes,
          }) as Product,
      );
      const productList = effectiveCountry
        ? mergedProducts.filter((product) => product.country === effectiveCountry)
        : mergedProducts;

      return productList;
    }),
  );

  feedAdvice$: Observable<FeedAdvice> = this.store.select(getSelectedFeedAdvice).pipe(
    withLatestFrom(
      this.store.select(getSelectedLocations),
      this.store.select(UserSelectors.selectUser),
      this.products$,
      this.store.select(getSelectedProductCountry),
      this.energySetting$,
      this.lysineSetting$,
      this.store.select(UserSelectors.selectLocation),
    ),
    take(1),
    map(
      ([
        selectedFeedAdvice,
        locationIds,
        user,
        products,
        country,
        energySystem,
        lysineSystem,
        location,
      ]: [
        FeedAdvice,
        number[],
        AgrUser,
        Product[],
        string,
        EnergySystem,
        LysineSystem,
        SelectedLocation,
      ]) =>
        this.createInitialAdvice(
          selectedFeedAdvice,
          locationIds,
          user,
          products,
          country,
          energySystem,
          lysineSystem,
          location,
        ),
    ),
  );
  feedAdviceType: FeedAdviceType;
  isReferenceFeedAdvice: boolean;
  showLysine: boolean;
  showEnergy: boolean;
  showDosator: boolean;
  advice: FeedAdvice;
  tableScheme: TableScheme;
  feedAdviceProperties: FeedAdviceProperties;
  phaseTransition: PhaseTransitionTypes;
  terminalBoars: string[] = Object.values(TerminalBoar);
  geneticLines: string[] = Object.values(GeneticLineType);
  terminalBoar = '';
  feedAdviceChanged = false;
  feedAdviceSettingsChanged = false;

  @ViewChild(FeedAdviceTableComponent) feedAdviceTableComponent: FeedAdviceTableComponent;
  @ViewChild(FeedAdviceSettingsComponent) settingsComponent: FeedAdviceSettingsComponent;

  constructor(
    private readonly store: Store<RootState>,
    private readonly router: Router,
    private readonly locale: LocaleService,
    private readonly schemeSupportService: SchemeSupportService,
    private readonly dialogService: DialogService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.activatedRoute.data.subscribe((response) => {
      this.isReferenceFeedAdvice = <boolean>response.isReferenceFeedAdvice;
    });
    this.activatedRoute.params.subscribe((params) => {
      this.feedAdviceType = <FeedAdviceType>params.feedAdviceType;
    });
  }

  getTitle(): string {
    const typeText = this.feedAdviceType
      ? `(${this.locale.translate('feedAdviceType.' + this.feedAdviceType.toLowerCase())})`
      : '';
    const titleText = this.locale.translate(
      `create-${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice.title`,
    );
    return `${titleText} ${typeText}`;
  }

  back() {
    if (this.feedAdviceChanged || this.feedAdviceSettingsChanged) {
      this.dialogService
        .pendingChanges({
          title: this.locale.translate('create-feed-advice.pending-changes-warning.title'),
          subTitle: this.locale.translate('create-feed-advice.pending-changes-warning.message'),
          pushHistoryState: false,
        })
        .subscribe((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.router.navigate([`/${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`]);
          }
        });
    } else {
      this.router.navigate([`/${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice`]);
    }
  }

  createInitialAdvice(
    selectedFeedAdvice: FeedAdvice,
    locationIds: number[],
    user: AgrUser,
    products: Product[],
    country: string,
    energySystem: EnergySystem,
    lysineSystem: LysineSystem,
    location: SelectedLocation,
  ): FeedAdvice {
    const createInitialFatteningRearingFeedAdviceLines = (): FeedAdviceLine[] => {
      const lines = <FeedAdviceLine[]>[];
      const numberOfLines = this.feedAdviceType === FeedAdviceType.FATTENING ? 20 : 30;
      for (let week = 1; week <= numberOfLines; week++) {
        lines.push(<FeedAdviceLine>{
          week,
          weekAge: week - 1,
          age: week * 7 - 6,
          weight: null,
          energyValuePerDay: null,
          intestinalDigestibleLysinePerDay: null,
          cumulativeFoodInKg: null,
          foodPerDay: null,
          consumptions: [{ phaseNumber: 1 }, { phaseNumber: 2 }],
        });
      }
      return lines;
    };
    const createInitialWeanedPigletFeedAdviceLines = (): FeedAdviceLine[] => {
      const lines = <FeedAdviceLine[]>[];
      for (let day = 1; day <= 49; day++) {
        lines.push(<FeedAdviceLine>{
          day,
          weight: null,
          age: null,
          energyValuePerDay: null,
          intestinalDigestibleLysinePerDay: null,
          cumulativeFoodInKg: null,
          foodPerDay: null,
          consumptions: [{ phaseNumber: 1 }, { phaseNumber: 2 }],
        });
      }
      return lines;
    };
    const effectiveCountry = country ?? location?.country;

    if (
      products.length === 0 ||
      (effectiveCountry ? products.some((product) => product.country !== effectiveCountry) : false)
    ) {
      if (selectedFeedAdvice?.phases) {
        selectedFeedAdvice = {
          ...selectedFeedAdvice,
          phases: selectedFeedAdvice?.phases.map((p) => (p = { ...p, product: null as Product })),
        };
      }
      if (this.feedAdviceTableComponent?.phases) {
        this.feedAdviceTableComponent.phases = this.feedAdviceTableComponent.phases.map(
          (p) => (p = { ...p, product: null as Product }),
        );
      }
    }

    this.advice = {
      id: 0,
      isReferenceFeedAdvice: this.isReferenceFeedAdvice,
      description: selectedFeedAdvice ? selectedFeedAdvice.description : '',
      usedReferenceFeedAdviceId: selectedFeedAdvice
        ? selectedFeedAdvice.usedReferenceFeedAdviceId
        : null,
      locationIds: selectedFeedAdvice ? selectedFeedAdvice.locationIds : locationIds,
      advisorName: user.name,
      type: this.feedAdviceType,
      feedAdviceType: this.feedAdviceType,
      name: selectedFeedAdvice ? selectedFeedAdvice.name : '',
      terminalBoar: selectedFeedAdvice ? selectedFeedAdvice.terminalBoar : '',
      isBarrow: selectedFeedAdvice ? selectedFeedAdvice.isBarrow : false,
      isBoar: selectedFeedAdvice ? selectedFeedAdvice.isBoar : false,
      isGilt: selectedFeedAdvice ? selectedFeedAdvice.isGilt : false,
      isSow: selectedFeedAdvice ? selectedFeedAdvice.isSow : false,
      phaseTransition: selectedFeedAdvice
        ? selectedFeedAdvice.phaseTransition
        : PhaseTransitionTypes.GRADUAL,
      numberOfPhases: selectedFeedAdvice ? selectedFeedAdvice.numberOfPhases : 2,
      inputSystem: selectedFeedAdvice?.inputSystem ?? InputSystemType.ENERGYDAY,
      phases: selectedFeedAdvice
        ? selectedFeedAdvice.phases.map((phase) => ({
            ...phase,
            product: {
              ...phase.product,
              name: products.find((p) => p.code === phase.product?.code)?.name,
              lysKg: products.find((p) => p.code === phase.product?.code)?.lysKg,
              ewKg: products.find((p) => p.code === phase.product?.code)?.ewKg,
            },
          }))
        : [{ phaseNumber: 1 }, { phaseNumber: 2 }],
      updated: null,
      advisorProfileId: null,
      customerId: null,
      energySystem,
      lysineSystem,
      country: this.isReferenceFeedAdvice ? country : null,
      fatteningRearingFeedAdviceLines: [],
      sowFeedAdviceLines: [],
      weanedPigletFeedAdviceLines: [],
      showDosatorColumns: false,
    };

    switch (this.feedAdviceType) {
      case FeedAdviceType.REARING:
        this.advice = {
          ...this.advice,
          feedingType: selectedFeedAdvice ? selectedFeedAdvice.feedingType : FeedingType.CDI,
          rearingAge: selectedFeedAdvice ? selectedFeedAdvice.rearingAge : 0,
          feedIntake: selectedFeedAdvice ? selectedFeedAdvice.feedIntake : FeedIntakeTypes.LOW,
          geneticLine: selectedFeedAdvice ? selectedFeedAdvice.geneticLine : null,
          selectedDosatorSettingsId: selectedFeedAdvice
            ? selectedFeedAdvice.selectedDosatorSettingsId
            : null,
          fatteningRearingFeedAdviceLines: selectedFeedAdvice
            ? selectedFeedAdvice.fatteningRearingFeedAdviceLines.map((feedAdviceLine) => ({
                ...feedAdviceLine,
                referenceSchemeIntestinalDigestibleLysinePerDay:
                  feedAdviceLine.intestinalDigestibleLysinePerDay,
                foodPerDay: this.schemeSupportService.calculateFeedPerDay(
                  feedAdviceLine,
                  selectedFeedAdvice.phases,
                  InputSystemType.ENERGYDAY,
                  false,
                ),
              }))
            : createInitialFatteningRearingFeedAdviceLines(),
        };
        break;

      case FeedAdviceType.FATTENING:
        this.advice = {
          ...this.advice,
          feedIntake: selectedFeedAdvice ? selectedFeedAdvice.feedIntake : FeedIntakeTypes.LOW,
          fatteningRearingFeedAdviceLines: selectedFeedAdvice
            ? selectedFeedAdvice.fatteningRearingFeedAdviceLines.map((feedAdviceLine) => ({
                ...feedAdviceLine,
                referenceSchemeIntestinalDigestibleLysinePerDay:
                  feedAdviceLine.intestinalDigestibleLysinePerDay,
                foodPerDay: this.schemeSupportService.calculateFeedPerDay(
                  feedAdviceLine,
                  selectedFeedAdvice.phases,
                  InputSystemType.ENERGYDAY,
                  false,
                ),
              }))
            : createInitialFatteningRearingFeedAdviceLines(),
        };
        break;

      case FeedAdviceType.WEANED_PIGLETS:
        this.advice = {
          ...this.advice,
          weaningAge: selectedFeedAdvice ? selectedFeedAdvice.weaningAge : 0,
          weaningWeight: selectedFeedAdvice ? selectedFeedAdvice.weaningWeight : 0,
          geneticLine: selectedFeedAdvice ? selectedFeedAdvice.geneticLine : null,
          weanedPigletFeedAdviceLines: selectedFeedAdvice
            ? selectedFeedAdvice.weanedPigletFeedAdviceLines.map((feedAdviceLine) => ({
                ...feedAdviceLine,
                referenceSchemeIntestinalDigestibleLysinePerDay:
                  feedAdviceLine.intestinalDigestibleLysinePerDay,
                foodPerDay: this.schemeSupportService.calculateFeedPerDay(
                  feedAdviceLine,
                  selectedFeedAdvice.phases,
                  InputSystemType.ENERGYDAY,
                  true,
                ),
              }))
            : createInitialWeanedPigletFeedAdviceLines(),
        };
        break;
    }

    this.feedAdviceProperties = <FeedAdviceProperties>{
      phaseTransition: this.advice.phaseTransition,
      terminalBoar: this.advice.terminalBoar,
      geneticLine: this.advice.geneticLine,
      feedAdviceType: this.feedAdviceType,
      genders: this.schemeSupportService.getGendersArray(
        this.advice.isBarrow,
        this.advice.isBoar,
        this.advice.isGilt,
      ),
      feedintake: this.advice.feedIntake,
      phases: this.advice.numberOfPhases,
      inputSystem: this.advice.inputSystem,
      selectedDosatorSettingsId: this.advice.selectedDosatorSettingsId,
      feedingType: this.advice.feedingType,
    };

    this.tableScheme = {
      phases: this.advice.phases,
      numberOfPhases: this.advice.numberOfPhases,
      phaseTransition: this.advice.phaseTransition,
      feedAdviceType: this.advice.feedAdviceType,
      feedAdviceLines:
        this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
          ? this.advice.weanedPigletFeedAdviceLines
          : this.advice.fatteningRearingFeedAdviceLines,
      inputSystem: this.advice.inputSystem,
      pigletWeaningAge:
        this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS ? this.advice.weaningAge : null,
      rearingAge: this.feedAdviceType === FeedAdviceType.REARING ? this.advice.rearingAge : null,
      usedReferenceFeedAdviceId: this.advice.usedReferenceFeedAdviceId,
    };
    return this.advice;
  }

  changeFeedAdviceSettings(emitted: { locations: number[]; properties: FeedAdviceProperties }) {
    const isGender = (genderType: string): boolean =>
      emitted.properties.genders
        ? emitted.properties.genders.some(
            (name) => name === this.locale.translate(`gender.${genderType}`).toLowerCase(),
          )
        : false;
    this.advice = {
      ...this.advice,
      locationIds: emitted.locations,
      phaseTransition: emitted.properties.phaseTransition,
      numberOfPhases: emitted.properties.phases,
      terminalBoar: emitted.properties.terminalBoar,
      geneticLine: emitted.properties.geneticLine,
      feedAdviceType: emitted.properties.feedAdviceType,
      type: emitted.properties.feedAdviceType,
      feedIntake: emitted.properties.feedintake,
      isBarrow: isGender(GenderTypes.BARROW),
      isBoar: isGender(GenderTypes.BOAR),
      isGilt: isGender(GenderTypes.GILT),
      inputSystem: emitted.properties.inputSystem,
      weaningWeight: emitted.properties.weaningWeight,
      weaningAge: emitted.properties.weaningAge,
      rearingAge: emitted.properties.rearingAge,
      showDosatorColumns: emitted.properties.showDosator,
      selectedDosatorSettingsId: emitted.properties.selectedDosatorSettingsId,
      feedingType: emitted.properties.feedingType,
    };
    this.showLysine = emitted.properties.showLysine;
    this.showEnergy = emitted.properties.showEnergy;
    this.showDosator = emitted.properties.showDosator;

    if (this.feedAdviceTableComponent) {
      this.feedAdviceTableComponent.updateFeedAdviceProperties(emitted.properties);
    }

    this.feedAdviceChanged = true;
  }

  getButtonText(): string {
    return this.locale.translate(
      `${this.isReferenceFeedAdvice ? 'reference-' : ''}feed-advice-save`,
    );
  }

  openPriceSettings() {
    this.dialogService
      .form(PriceSettingsComponent, {
        phases: this.feedAdviceTableComponent.phases,
        feedAdvice: this.advice,
      })
      .subscribe((result) => {
        if (result.result?.phases && this.feedAdviceTableComponent) {
          this.feedAdviceTableComponent.phases = result.result?.phases;
          this.feedAdviceSettingsChanged = true;
        }
      });
  }

  save() {
    this.settingsComponent.propertiesComponent.formGroup.updateValueAndValidity();
    this.settingsComponent.propertiesComponent.formGroup.markAllAsTouched();
    this.settingsComponent.propertiesComponent.formGroupRearingAge.updateValueAndValidity();
    this.settingsComponent.propertiesComponent.formGroupRearingAge.markAllAsTouched();

    this.feedAdviceTableComponent.validateTable();
    if (
      this.schemeSupportService.validateFeedAdvice(
        this.advice,
        this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS,
        this.feedAdviceType === FeedAdviceType.REARING,
        this.feedAdviceTableComponent,
        this.settingsComponent,
      )
    ) {
      this.energySetting$
        .pipe(
          withLatestFrom(this.lysineSetting$),
          skipWhile(([energy, lysine]) => energy === null || lysine === null),
          take(1),
        )
        .subscribe(([energySystem, lysineSystem]) => {
          this.advice = <FeedAdvice>{
            ...this.advice,
            energySystem,
            lysineSystem,
            phases: this.feedAdviceTableComponent.phases,
            isReferenceFeedAdvice: this.isReferenceFeedAdvice,
            fatteningRearingFeedAdviceLines:
              this.feedAdviceType === FeedAdviceType.FATTENING ||
              this.feedAdviceType === FeedAdviceType.REARING
                ? this.feedAdviceTableComponent.tableRows
                : [],
            weanedPigletFeedAdviceLines:
              this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS
                ? this.feedAdviceTableComponent.tableRows
                : [],
            showDosatorColumns: this.showDosator,
            finalWeight: this.feedAdviceTableComponent.finalBodyWeightFormGroup.value.finalWeight,
          };

          this.store.dispatch(
            upsertFeedAdviceRequest({
              request: this.advice,
              isReferenceFeedAdvice: this.isReferenceFeedAdvice,
            }),
          );
        });
    }
  }
}
